import { computed } from 'vue'
import { useStore } from 'vuex'
import { formatAmount } from 'foundation/js/utils/numberUtils'
import { buildFundraisingTargetUrl } from 'foundation/js/utils/fundraisingFormAnchors'
import PersonalFundraisingDashboardMenu from 'components/PersonalFundraisingDashboardMenu/PersonalFundraisingDashboardMenu.vue'

const SHARE_OVERLAY = 'PersonalFundraisingShareOverlay'
const TITLE_MAX_LENGTH = 60
const DATE_PLACEHOLDER = '{date}'

export default {
    name: 'PersonalFundraisingActiveCard',
    components: {
        PersonalFundraisingDashboardMenu
    },
    props: {
        name: {
            type: String,
            required: true
        },
        url: {
            type: String,
            default: null
        },
        editUrl: {
            type: String,
            default: null
        },
        campaignId: {
            type: String,
            default: null
        },
        qrCodeUrl: {
            type: String,
            default: null
        },
        qrCodeImage: {
            type: String,
            default: null
        },
        livestreamUrl: {
            type: String,
            default: null
        },
        amount: {
            type: Number,
            default: 0
        },
        targetAmount: {
            type: Number,
            default: 0
        },
        editLabel: {
            type: String,
            default: 'Rediger indsamling'
        },
        moreOptionsLabel: {
            type: String,
            default: 'Flere muligheder'
        },
        updateTargetLabel: {
            type: String,
            default: 'Opdater indsamlingsmål'
        },
        qrCodeLabel: {
            type: String,
            default: 'QR-kode'
        },
        livestreamLabel: {
            type: String,
            default: 'Livestream'
        },
        remainingLabelTemplate: {
            type: String,
            default: 'Du er kun {amount} fra at nå dit mål'
        },
        shareCtaLabel: {
            type: String,
            default: 'Del indsamlingen'
        },
        shareSubtext: {
            type: String,
            default: 'Del igen og kom tættere på målet'
        },
        endDate: {
            type: String,
            default: null
        },
        linkCopiedLabel: {
            type: String,
            default: 'Dit link er kopieret!'
        },
        shareOverlayTitle: {
            type: String,
            default: 'Del indsamlingen i dit netværk'
        },
        shareOverlayDescription: {
            type: String,
            default: ''
        },
        shareNetworkTemplate: {
            type: String,
            default: 'Del på {network}'
        },
        shareCopyLinkLabel: {
            type: String,
            default: 'Kopier link'
        },
        shareGoToCollectionLabel: {
            type: String,
            default: 'Gå til indsamlingen'
        },
        isMemorial: {
            type: Boolean,
            default: false
        },
        donorCount: {
            type: Number,
            default: 0
        },
        collectedAmountLabel: {
            type: String,
            default: 'Du har indsamlet'
        },
        donorCountTemplate: {
            type: String,
            default: '{count} personer har doneret'
        }
    },
    setup(props) {
        const store = useStore()

        const truncatedName = computed(() => {
            const value = props.name?.trim() || ''
            if (value.length <= TITLE_MAX_LENGTH) {
                return value
            }
            return `${value.slice(0, TITLE_MAX_LENGTH).trimEnd()}…`
        })

        const hasTarget = computed(() => Number(props.targetAmount) > 0)

        const showMenu = computed(() => !props.isMemorial && (!!props.qrCodeImage || !!props.livestreamUrl))

        const fundraisingTargetUrl = computed(() =>
            buildFundraisingTargetUrl(props.editUrl)
        )

        const showUpdateTargetLink = computed(
            () =>
                !showMenu.value &&
                !props.isMemorial &&
                !!fundraisingTargetUrl.value
        )

        const progress = computed(() => {
            if (!hasTarget.value) {
                return 0
            }
            return Math.min((Number(props.amount) * 100) / Number(props.targetAmount), 100)
        })

        const progressMarkerPosition = computed(() => progress.value)

        const progressMarkerEdge = computed(() => {
            if (progress.value <= 0) {
                return 'start'
            }
            if (progress.value >= 100) {
                return 'end'
            }
            return null
        })

        const formattedAmount = computed(
            () => `${formatAmount(props.amount, 0)} kr.`
        )

        const formattedTargetAmount = computed(
            () => `${formatAmount(props.targetAmount, 0)} kr.`
        )

        const remainingAmountLabel = computed(() => {
            const remaining = Math.max(
                0,
                Number(props.targetAmount) - Number(props.amount)
            )
            return `${formatAmount(remaining, 0)} kr.`
        })

        const remainingLabelParts = computed(() => {
            const template =
                props.remainingLabelTemplate || 'Du er {amount} fra at nå dit mål'
            const [before = '', after = ''] = template.split('{amount}')
            return {
                before,
                amount: remainingAmountLabel.value,
                after
            }
        })

        const formattedEndDate = computed(() => {
            if (!props.endDate) {
                return null
            }
            const parsed = new Date(props.endDate)
            if (Number.isNaN(parsed.getTime())) {
                return null
            }
            const day = String(parsed.getDate()).padStart(2, '0')
            const month = String(parsed.getMonth() + 1).padStart(2, '0')
            return `${day}/${month}`
        })

        const resolvedShareSubtext = computed(() => {
            const template = props.shareSubtext || ''
            const date = props.isMemorial ? null : formattedEndDate.value
            return template
                .replace(DATE_PLACEHOLDER, date ?? '')
                .replace(/\s{2,}/g, ' ')
                .trim()
        })

        const donorCountParts = computed(() => {
            if (!props.isMemorial) {
                return null
            }
            const count = Math.max(0, Number(props.donorCount) || 0)
            const template =
                props.donorCountTemplate || '{count} personer har doneret'
            const [before = '', after = ''] = template.split('{count}')
            return {
                before,
                count: String(count),
                after
            }
        })

        const absoluteShareUrl = computed(() => {
            if (!props.url) {
                return typeof window !== 'undefined' ? window.location.href : ''
            }
            try {
                return new URL(props.url, window.location.origin).toString()
            } catch {
                return props.url
            }
        })

        const openShareOverlay = () => {
            store.commit('main/set_page_overlay_content_data', {
                shareUrl: absoluteShareUrl.value,
                campaignUrl: props.url,
                title: props.shareOverlayTitle,
                description: props.shareOverlayDescription,
                networkTemplate: props.shareNetworkTemplate,
                copyLinkLabel: props.shareCopyLinkLabel,
                linkCopiedLabel: props.linkCopiedLabel,
                goToCollectionLabel: props.shareGoToCollectionLabel
            })
            store.dispatch('main/openPageOverlay', {
                overlayContent: SHARE_OVERLAY
            })
        }

        return {
            shareCtaLabel: computed(() => props.shareCtaLabel),
            moreOptionsLabel: computed(() => props.moreOptionsLabel),
            showMenu,
            showUpdateTargetLink,
            fundraisingTargetUrl,
            truncatedName,
            hasTarget,
            progress,
            progressMarkerPosition,
            progressMarkerEdge,
            formattedAmount,
            formattedTargetAmount,
            remainingLabelParts,
            resolvedShareSubtext,
            donorCountParts,
            isMemorial: computed(() => props.isMemorial),
            collectedAmountLabel: computed(() => props.collectedAmountLabel),
            openShareOverlay
        }
    }
}
